import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { TBillingHistory, TGetBillingHistory, TGetBillingHistoryParams } from '@/typings/api/billing_histories.type'
import { TGroupBilling } from '@/typings/api/group_billings.type'
import { TGuestUser, TUser } from '@/typings/api/group_users.type'
import { TGroup } from '@/typings/api/groups.type'
import { TGetTemplate, TGetTemplateParams } from '@/typings/api/message_template.type'
import { TCampaignAccount } from '@/typings/api/twitter_campaign_accounts.type'
import { TGetNgApplicantParams, TNgApplicant } from '@/typings/api/twitter_ng_applicants.type'

type SettingGroupFetchResult = {
  shouldLeaveGroupSetting: boolean
}

type SettingGroupState = {
  accounts: TCampaignAccount[]
  groupSettingUnauthenticatedAccounts: TCampaignAccount[]
  remainAccountsCount: number
  users: TUser[]
  guestUsers: TGuestUser[]
  remainUsersCount: number
  remainGuestUsersCount: number
  templates: TGetTemplate[]
  templateTotal: number
  templateParam: TGetTemplateParams
  ngApplicants: TNgApplicant[]
  ngApplicantParam: TGetNgApplicantParams
  group: TGroup | null
  ngApplicantTotal: number
  billingHistories: TBillingHistory[]
  groupBilling: TGroupBilling
  billingHistoryTotal: number
  billingHistoryParam: TGetBillingHistoryParams
  billingHistoryStartMonth: Date | null
  billingHistoryEndMonth: Date | null
  error: boolean
}

const createInitialState = (): SettingGroupState => ({
  accounts: [],
  groupSettingUnauthenticatedAccounts: [],
  remainAccountsCount: 0,
  users: [],
  remainUsersCount: 0,
  guestUsers: [],
  remainGuestUsersCount: 0,
  templates: [],
  templateTotal: 0,
  templateParam: {
    groupId: 0,
    sortBy: '-name',
    pagingNo: 1,
    limitCount: 25
  },
  ngApplicantTotal: 0,
  ngApplicants: [],
  ngApplicantParam: {
    groupId: 0,
    sortBy: '-created',
    pagingNo: 1,
    limitCount: 25,
    screenName: null
  },
  group: null,
  billingHistories: [],
  groupBilling: {
    groupId: 0,
    group: {
      name: ''
    },
    companyName: '',
    departName: null,
    personName: null,
    email: null,
    address: null,
    countryCode: null,
    zipCode: null,
    telephone: null
  },
  billingHistoryTotal: 0,
  billingHistoryParam: {
    sortBy: '-target_month',
    pagingNo: 1,
    limitCount: 25
  },
  billingHistoryStartMonth: null,
  billingHistoryEndMonth: null,
  error: false
})

export const useSettingGroupStore = defineStore('settingGroupStore', () => {
  const initialState = createInitialState()

  const accounts = ref<SettingGroupState['accounts']>(initialState.accounts)
  const groupSettingUnauthenticatedAccounts = ref<SettingGroupState['groupSettingUnauthenticatedAccounts']>(
    initialState.groupSettingUnauthenticatedAccounts
  )
  const remainAccountsCount = ref<SettingGroupState['remainAccountsCount']>(initialState.remainAccountsCount)
  const users = ref<SettingGroupState['users']>(initialState.users)
  const guestUsers = ref<SettingGroupState['guestUsers']>(initialState.guestUsers)
  const remainUsersCount = ref<SettingGroupState['remainUsersCount']>(initialState.remainUsersCount)
  const remainGuestUsersCount = ref<SettingGroupState['remainGuestUsersCount']>(initialState.remainGuestUsersCount)
  const templates = ref<SettingGroupState['templates']>(initialState.templates)
  const templateTotal = ref<SettingGroupState['templateTotal']>(initialState.templateTotal)
  const templateParam = ref<SettingGroupState['templateParam']>(initialState.templateParam)
  const ngApplicants = ref<SettingGroupState['ngApplicants']>(initialState.ngApplicants)
  const ngApplicantParam = ref<SettingGroupState['ngApplicantParam']>(initialState.ngApplicantParam)
  const group = ref<SettingGroupState['group']>(initialState.group)
  const ngApplicantTotal = ref<SettingGroupState['ngApplicantTotal']>(initialState.ngApplicantTotal)
  const billingHistories = ref<SettingGroupState['billingHistories']>(initialState.billingHistories)
  const groupBilling = ref<SettingGroupState['groupBilling']>(initialState.groupBilling)
  const billingHistoryTotal = ref<SettingGroupState['billingHistoryTotal']>(initialState.billingHistoryTotal)
  const billingHistoryParam = ref<SettingGroupState['billingHistoryParam']>(initialState.billingHistoryParam)
  const billingHistoryStartMonth = ref<SettingGroupState['billingHistoryStartMonth']>(
    initialState.billingHistoryStartMonth
  )
  const billingHistoryEndMonth = ref<SettingGroupState['billingHistoryEndMonth']>(initialState.billingHistoryEndMonth)
  const error = ref<SettingGroupState['error']>(initialState.error)

  const hasValidGroupId = (groupId: number | undefined | null): groupId is number => {
    return typeof groupId === 'number' && groupId > 0
  }

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    accounts.value = nextState.accounts
    groupSettingUnauthenticatedAccounts.value = nextState.groupSettingUnauthenticatedAccounts
    remainAccountsCount.value = nextState.remainAccountsCount
    users.value = nextState.users
    guestUsers.value = nextState.guestUsers
    remainUsersCount.value = nextState.remainUsersCount
    remainGuestUsersCount.value = nextState.remainGuestUsersCount
    templates.value = nextState.templates
    templateTotal.value = nextState.templateTotal
    templateParam.value = nextState.templateParam
    ngApplicants.value = nextState.ngApplicants
    ngApplicantParam.value = nextState.ngApplicantParam
    group.value = nextState.group
    ngApplicantTotal.value = nextState.ngApplicantTotal
    billingHistories.value = nextState.billingHistories
    groupBilling.value = nextState.groupBilling
    billingHistoryTotal.value = nextState.billingHistoryTotal
    billingHistoryParam.value = nextState.billingHistoryParam
    billingHistoryStartMonth.value = nextState.billingHistoryStartMonth
    billingHistoryEndMonth.value = nextState.billingHistoryEndMonth
    error.value = nextState.error
  }

  const fetchAccounts = async (groupId: number): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get('/campaign_accounts', {
      params: {
        groupId
      }
    })

    if (!result.data && result.error && result.error.type === 'PERMISSION_DENIED') {
      return { shouldLeaveGroupSetting: true }
    }

    if (!result.data) {
      error.value = true
      return { shouldLeaveGroupSetting: false }
    }

    accounts.value = result.data.accounts
    remainAccountsCount.value = result.data.remainAccountsCount
    groupSettingUnauthenticatedAccounts.value = result.data.unauthenticatedAccounts

    return { shouldLeaveGroupSetting: false }
  }

  const fetchUsers = async (groupId: number): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get('/group_users', {
      params: {
        groupId
      }
    })

    if (!result.data && result.error && result.error.type === 'PERMISSION_DENIED') {
      return { shouldLeaveGroupSetting: true }
    }

    if (!result.data) {
      error.value = true
      return { shouldLeaveGroupSetting: false }
    }

    users.value = result.data.users
    remainUsersCount.value = result.data.remainUsersCount

    return { shouldLeaveGroupSetting: false }
  }

  const fetchGuestUsers = async (groupId: number): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get('/group_guest_users', {
      params: {
        groupId
      }
    })

    if (!result.data && result.error && result.error.type === 'PERMISSION_DENIED') {
      return { shouldLeaveGroupSetting: true }
    }

    if (!result.data) {
      error.value = true
      return { shouldLeaveGroupSetting: false }
    }

    guestUsers.value = result.data.guestUsers
    remainGuestUsersCount.value = result.data.remainGuestUsersCount

    return { shouldLeaveGroupSetting: false }
  }

  const fetchTemplates = async (): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(templateParam.value.groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get('/message_templates', {
      params: templateParam.value
    })

    // サーバーが返すのは PERMISSION_DENIED のため実質デッドコードだが、既存の挙動を変えないため条件はそのまま残す
    if (!result.data && result.error && result.error.type === 'GROUP_ADMIN_ROLE_NOT_EXISTS') {
      return { shouldLeaveGroupSetting: true }
    }

    if (!result.data) {
      error.value = true
      return { shouldLeaveGroupSetting: false }
    }

    templates.value = result.data.templates
    templateTotal.value = result.data.dataCount

    return { shouldLeaveGroupSetting: false }
  }

  const fetchNgApplicants = async (snsType: string): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(ngApplicantParam.value.groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get(`/${snsType}_ng_applicants`, {
      params: ngApplicantParam.value
    })

    if (!result.data && result.error && result.error.type === 'PERMISSION_DENIED') {
      return { shouldLeaveGroupSetting: true }
    }

    if (!result.data) {
      error.value = true
      return { shouldLeaveGroupSetting: false }
    }

    ngApplicants.value = result.data.ngApplicants
    ngApplicantTotal.value = result.data.dataCount

    return { shouldLeaveGroupSetting: false }
  }

  const fetchGroup = async (groupId: number): Promise<void> => {
    if (!hasValidGroupId(groupId)) {
      return
    }

    error.value = false

    const result = await api.get(`/groups/${groupId}`)

    if (!result.data) {
      error.value = true
      return
    }

    group.value = result.data
  }

  const fetchBillingHistories = async (groupId: number): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get(`/billing_histories/${groupId}`, {
      params: {
        ...billingHistoryParam.value
      }
    })

    if (!result.data && result.error && result.error.type === 'PERMISSION_DENIED') {
      return { shouldLeaveGroupSetting: true }
    }

    if (!result.data) {
      return { shouldLeaveGroupSetting: false }
    }

    const data = result.data as TGetBillingHistory
    billingHistories.value = data.billingHistories
    billingHistoryTotal.value = data.dataCount
    billingHistoryStartMonth.value = data.startContractMonth
    billingHistoryEndMonth.value = data.endContractMonth

    return { shouldLeaveGroupSetting: false }
  }

  const fetchGroupBilling = async (groupId: number): Promise<SettingGroupFetchResult> => {
    if (!hasValidGroupId(groupId)) {
      return { shouldLeaveGroupSetting: false }
    }

    error.value = false

    const result = await api.get(`/v1/group_billings/${groupId}`)

    // ここだけエラーの種別を問わず離脱する。既存の挙動を変えないため条件はそのまま残す
    if (!result || !result.data || result.error) {
      return { shouldLeaveGroupSetting: true }
    }

    groupBilling.value = result.data

    return { shouldLeaveGroupSetting: false }
  }

  const setParamTemplate = (params: TGetTemplateParams): void => {
    templateParam.value = {
      ...templateParam.value,
      ...params,
      pagingNo: params.pagingNo || 1
    }
  }

  const setParamNgApplicant = (params: TGetNgApplicantParams): void => {
    ngApplicantParam.value = {
      ...ngApplicantParam.value,
      ...params,
      pagingNo: params.pagingNo || 1
    }
  }

  const setParamBillingHistory = (params: TGetBillingHistoryParams): void => {
    billingHistoryParam.value = {
      ...billingHistoryParam.value,
      ...params,
      pagingNo: params.pagingNo || 1
    }
  }

  return {
    accounts: readonly(accounts),
    groupSettingUnauthenticatedAccounts: readonly(groupSettingUnauthenticatedAccounts),
    remainAccountsCount: readonly(remainAccountsCount),
    users: readonly(users),
    guestUsers: readonly(guestUsers),
    remainUsersCount: readonly(remainUsersCount),
    remainGuestUsersCount: readonly(remainGuestUsersCount),
    templates: readonly(templates),
    templateTotal: readonly(templateTotal),
    templateParam: readonly(templateParam),
    ngApplicants: readonly(ngApplicants),
    ngApplicantParam: readonly(ngApplicantParam),
    group: readonly(group),
    ngApplicantTotal: readonly(ngApplicantTotal),
    billingHistories: readonly(billingHistories),
    groupBilling: readonly(groupBilling),
    billingHistoryTotal: readonly(billingHistoryTotal),
    billingHistoryParam: readonly(billingHistoryParam),
    billingHistoryStartMonth: readonly(billingHistoryStartMonth),
    billingHistoryEndMonth: readonly(billingHistoryEndMonth),
    error: readonly(error),
    reset,
    fetchAccounts,
    fetchUsers,
    fetchGuestUsers,
    fetchTemplates,
    fetchNgApplicants,
    fetchGroup,
    fetchBillingHistories,
    fetchGroupBilling,
    setParamTemplate,
    setParamNgApplicant,
    setParamBillingHistory
  }
})
